import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePageMeta } from '@/hooks/usePageMeta';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import carrierProfessional from '@/assets/carrier-professional.jpg';
import carrierModernLoadBoard from '@/assets/carrier-modern-load-board.jpg';
import carrierFullPicture from '@/assets/carrier-full-picture.jpg';
import atlasLogo from '@/assets/atlas-assistant-logo.png';
import PostTruckMockup from '@/components/marketing/mockups/PostTruckMockup';
import TrustScoreMockup from '@/components/marketing/mockups/TrustScoreMockup';
import {
  ArrowRight, Check, Sparkles, Map, BarChart3, Shield, Brain,
  Mic, Target, Zap, Star, TrendingUp, Truck, Building2, Search,
  FileCheck2, Award, Package, ClipboardCheck, FileText, Route,
} from 'lucide-react';

const TEAL = '#02857E';

const CarriersPage: React.FC = () => {
  usePageMeta({
    fullTitle: true,
    title: 'For Carriers | LaneRunner',
    description:
      'Search broker-posted loads or compete for freight directly from shippers. Route, rate, stops, company data and market intelligence before you commit. $99.99/mo.',
  });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <LandingHeader activePage="carriers" />

      {/* ============ HERO ============ */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-br from-[#f0f7f6] via-white to-[#f5faf9] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(${TEAL} 1px, transparent 1px), linear-gradient(90deg, ${TEAL} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl" style={{ background: `${TEAL}1A` }} />

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-5 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#02857E]/30 text-[#02857E] text-xs font-semibold tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" /> BUILT FOR CARRIERS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5">
              Find better freight.
              <br />
              <span style={{ color: TEAL }}>Build better relationships.</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl">
              Search thousands of broker-posted loads or compete for freight directly from shippers.
              LaneRunner gives you the rate, route, stops, company information, and market intelligence
              before you commit — so you spend less time chasing freight and more time moving it.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Button
                asChild
                size="lg"
                className="rounded-full text-white font-semibold px-7 shadow-lg hover:opacity-90"
                style={{ backgroundColor: TEAL }}
              >
                <Link to="/auth?role=carrier">
                  Get Started <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-[#02857E]/40 text-[#02857E] hover:bg-[#02857E]/10 font-semibold px-7"
              >
                <Link to="/pricing?role=carrier">View Carrier Plans <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-700">
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: TEAL }} /> Direct shipper freight</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: TEAL }} /> Real broker loads</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: TEAL }} /> Market rate intelligence</span>
            </div>
            <p className="mt-4 text-sm font-semibold text-gray-900">Everything in one place. $99.99/mo.</p>
          </div>

          <div className="lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              <img src={carrierProfessional} alt="Truck driver using LaneRunner" className="w-full h-auto object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 2: TWO WAYS TO WIN FREIGHT ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-[#06171C] relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{ background: `radial-gradient(60% 50% at 50% 0%, ${TEAL}40 0%, transparent 70%)` }}
        />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-[#17E5DD] text-xs font-semibold tracking-wider mb-4">
              <Zap className="w-3.5 h-3.5" /> MORE THAN A LOAD BOARD
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">Two ways to win freight. One platform.</h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto leading-relaxed">
              You shouldn't have to choose between searching the spot market and building direct shipper
              relationships. LaneRunner gives you both.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* LEFT — Direct shipper freight */}
            <div className="rounded-2xl border border-[#02857E]/40 bg-white/[0.04] backdrop-blur-sm p-7 sm:p-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${TEAL}30` }}>
                <Building2 className="w-6 h-6 text-[#17E5DD]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Direct Shipper Freight</h3>
              <p className="text-base font-semibold text-[#17E5DD] mb-4">Get closer to the customer.</p>
              <p className="text-white/70 leading-relaxed mb-5">
                Get invited into shipper networks and compete for freight directly from the companies
                moving it. No cold calling. No chasing purchasing departments. No trying to figure out who
                handles transportation. When an opportunity fits your equipment and lanes, you can bid for
                it right inside LaneRunner.
              </p>
              <p className="text-sm font-semibold text-white mb-5">
                Controlled competition. Capped bidding. Better opportunities.
              </p>
              <ul className="space-y-2.5 mb-7">
                {[
                  'Direct shipper-posted freight',
                  'Controlled bid participation',
                  'Clearly identified carrier opportunities',
                  'Build relationships with shippers you perform for',
                  'Awards, messaging, dispatch and documents stay with the load',
                ].map((t) => (
                  <li key={t} className="flex gap-2 text-white/85 text-sm sm:text-base">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#17E5DD]" />{t}
                  </li>
                ))}
              </ul>
              <Button asChild className="rounded-full text-white font-semibold px-6" style={{ backgroundColor: TEAL }}>
                <Link to="/auth?role=carrier">Explore Shipper Freight <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>

            {/* RIGHT — modern load board */}
            <div className="rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-sm p-7 sm:p-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 bg-white/10">
                <Search className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">The Modern Load Board</h3>
              <p className="text-base font-semibold text-white/80 mb-4">
                And when you want broker freight, it's already here.
              </p>
              <p className="text-white/70 leading-relaxed mb-6">
                Search live freight from brokers across the market with the information you need to decide
                whether a load is worth your time. See the route. See the posted rate. See the equipment.
                See the broker. Then make the call.
              </p>
              <div className="rounded-xl overflow-hidden border border-white/10 mb-7">
                <img
                  src={carrierModernLoadBoard}
                  alt="Modern freight yard with clear lanes and transparent load access"
                  width={1344}
                  height={896}
                  loading="lazy"
                  className="w-full h-auto object-cover"
                />
              </div>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-white/40 text-white hover:bg-white/10 font-semibold px-6 bg-transparent"
              >
                <Link to="/load-board">Explore the Load Board <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 3 ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Target className="w-3.5 h-3.5" /> BUILT FOR HOW CARRIERS ACTUALLY BOOK FREIGHT
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Stop wasting time on loads that don't fit.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Map,
                title: 'See the whole load first',
                body: 'Origin, destination, every stop, total miles, equipment, dates and available rate information before you start chasing the load.',
              },
              {
                icon: BarChart3,
                title: 'Know what the lane is worth',
                body: 'LaneRunner gives you current market intelligence so you can recognize a strong rate, and know when there\u2019s room to negotiate.',
              },
              {
                icon: Shield,
                title: 'Know who you\u2019re working with',
                body: 'Review broker information, authority data and available company information before you commit your truck.',
              },
            ].map((c) => (
              <div key={c.title} className="p-6 rounded-2xl border border-gray-200 hover:border-[#02857E]/40 hover:shadow-lg transition-all bg-white">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${TEAL}15` }}>
                  <c.icon className="w-6 h-6" style={{ color: TEAL }} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-gray-600 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 4: ROUTE TRANSPARENCY ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
            <img
              src={carrierFullPicture}
              alt="Load confirmation sheet with all freight details visible upfront"
              width={1344}
              height={896}
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Route className="w-3.5 h-3.5" /> NO MORE "CALL FOR DETAILS"
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Every load shows the full picture.</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              A rate means nothing if you don't know there are four pickups hiding behind it. LaneRunner
              puts the important details up front so you can decide whether the freight actually works for
              your truck before wasting time on the phone.
            </p>
            <ul className="space-y-2.5">
              {[
                'Origin and destination',
                'Every pickup and delivery',
                'Total loaded miles',
                'Equipment and weight',
                'Commodity information',
                'Pickup and delivery windows',
                'Posted rate and rate per mile',
                'Broker or shipper information',
              ].map((t) => (
                <li key={t} className="flex gap-2 text-gray-800"><Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />{t}</li>
              ))}
            </ul>
            <p className="mt-6 text-base font-semibold text-gray-900">Know the load before you chase the load.</p>
          </div>
        </div>
      </section>

      {/* ============ SECTION 5: POST YOUR TRUCK ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Truck className="w-3.5 h-3.5" /> STOP SEARCHING FOR EVERY LOAD
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Let freight find you.</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Tell LaneRunner where your truck is, where you want to go, what you're pulling, and when
              you're available. We'll match your truck against relevant freight so you can spend less time
              refreshing load boards and more time evaluating opportunities that actually fit.
            </p>
            <p className="text-base font-semibold text-gray-900 mb-4">Post your truck manually — or just say it.</p>
            <div className="rounded-xl border border-[#02857E]/30 bg-[#02857E]/[0.06] p-5 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Mic className="w-4 h-4" style={{ color: TEAL }} />
                <span className="text-xs font-semibold tracking-wider" style={{ color: TEAL }}>VOICE POST</span>
              </div>
              <p className="text-gray-900 font-medium italic mb-2">
                "Reefer empty in Salinas tomorrow morning, looking for Northeast."
              </p>
              <p className="text-sm text-gray-600">
                LaneRunner turns it into a structured truck post in seconds.
              </p>
            </div>
            <Button asChild className="rounded-full text-white font-semibold px-6" style={{ backgroundColor: TEAL }}>
              <Link to="/auth?role=carrier">Post Your Truck <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
          <div className="order-1 lg:order-2">
            <PostTruckMockup />
          </div>
        </div>
      </section>

      {/* ============ SECTION 6: ATLAS ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <img src={atlasLogo} alt="ATLAS freight intelligence" className="h-14 w-14 mx-auto mb-4" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Brain className="w-3.5 h-3.5" /> ATLAS FREIGHT INTELLIGENCE
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Know more before you negotiate.</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              ATLAS works inside LaneRunner to help you understand the freight in front of you — not bury
              you in another dashboard.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: BarChart3, title: 'Rate Insights', body: 'See how the offered rate compares with the market.' },
              { icon: Target, title: 'Lane Intelligence', body: 'Understand what is happening on the lanes you actually run.' },
              { icon: Zap, title: 'Market Pressure', body: 'See where capacity conditions may give you more negotiating leverage.' },
              { icon: Star, title: 'Smart Matches', body: 'Surface freight that better fits your equipment, location and preferences.' },
              { icon: Mic, title: 'Voice Posting', body: 'Post your truck and interact with LaneRunner without typing every field.' },
              { icon: Search, title: 'Automated Dispatcher', body: 'Find loads from the board faster, with ATLAS surfacing the right opportunities for your truck and lanes.' },
            ].map((f) => (
              <div key={f.title} className="p-5 rounded-xl bg-white border border-gray-200 hover:border-[#02857E]/40 transition-colors">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${TEAL}15` }}>
                  <f.icon className="w-5 h-5" style={{ color: TEAL }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-base font-semibold text-gray-900 mt-8">
            Less searching. Better decisions. Faster booking.
          </p>
        </div>
      </section>

      {/* ============ SECTION 7: VERIFIED MARKETPLACE ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <TrustScoreMockup />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Shield className="w-3.5 h-3.5" /> VERIFIED MARKETPLACE
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Your truck is worth knowing who is on the other side.
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              LaneRunner gives you company and authority information before you book so you're not sending a
              $200,000 truck and trailer into a relationship completely blind.
            </p>
            <div className="space-y-4">
              {[
                { icon: FileCheck2, title: 'Authority information', body: 'See available operating-authority information before booking.' },
                { icon: Shield, title: 'Insurance information', body: 'View available insurance and compliance information.' },
                { icon: Building2, title: 'Company details', body: 'Know who you\u2019re communicating and doing business with.' },
                { icon: TrendingUp, title: 'Track record', body: 'Use available marketplace history and relationship information to make a better decision.' },
              ].map((p) => (
                <div key={p.title} className="flex gap-3">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${TEAL}15` }}>
                    <p.icon className="w-4.5 h-4.5" style={{ color: TEAL }} />
                  </span>
                  <div>
                    <div className="font-bold text-gray-900">{p.title}</div>
                    <p className="text-gray-600 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 8: ONE LOAD. ONE RECORD. ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
            <ClipboardCheck className="w-3.5 h-3.5" /> ONE LOAD. ONE RECORD.
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Booking it is only the beginning.</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            Once you win freight, the workflow doesn't disappear into email and text messages. Keep the load
            moving inside LaneRunner.
          </p>

          <div className="flex flex-wrap items-stretch justify-center gap-3 mb-8">
            {[
              { icon: Award, label: 'Award' },
              { icon: FileText, label: 'Dispatch' },
              { icon: Package, label: 'Pickup' },
              { icon: Truck, label: 'In Transit' },
              { icon: Map, label: 'Delivery' },
              { icon: FileCheck2, label: 'POD' },
            ].map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <div className="flex flex-col items-center gap-2 px-4 py-4 rounded-xl bg-white border border-gray-200 min-w-[104px]">
                  <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${TEAL}15` }}>
                    <s.icon className="w-5 h-5" style={{ color: TEAL }} />
                  </span>
                  <span className="text-sm font-semibold text-gray-900">{s.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <ArrowRight className="hidden sm:block self-center w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>

          <p className="text-base font-semibold text-gray-900">
            Messages, dispatch information, shipment details and documents stay connected to the load.
          </p>
        </div>
      </section>

      {/* ============ SECTION 9: PRICING ============ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#02857E]/10 text-[#02857E] text-xs font-semibold tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" /> FULL CARRIER ACCESS
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">$99.99/month.</h2>
            <p className="text-lg text-gray-600">
              One subscription gives carriers access to the LaneRunner platform.
            </p>
          </div>

          <div className="rounded-2xl border-2 border-[#02857E]/40 p-7 sm:p-9 bg-white shadow-lg">
            <ul className="space-y-3 text-gray-800 mb-7">
              {[
                'Search broker freight.',
                'Compete for direct shipper freight.',
                'Post your truck.',
                'Use ATLAS intelligence.',
                'Manage awarded freight.',
              ].map((t) => (
                <li key={t} className="flex gap-2"><Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />{t}</li>
              ))}
            </ul>
            <p className="text-sm font-semibold text-gray-900 mb-6">No stripped-down "free" carrier tier.</p>
            <Button asChild size="lg" className="w-full rounded-full text-white font-semibold" style={{ backgroundColor: TEAL }}>
              <Link to="/auth?role=carrier">Get Started <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <p className="text-center text-sm text-gray-600 mt-6">
              <Link to="/pricing?role=carrier" className="text-[#02857E] font-semibold hover:underline">
                View full plan comparison →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${TEAL} 0%, #014c47 100%)` }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
            Your next load could come from a broker. Or directly from the shipper.
          </h2>
          <p className="text-xl text-white font-semibold mb-4">Be ready for both.</p>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Search the market, post your truck, compete for direct opportunities and make better booking
            decisions from one platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="rounded-full bg-white text-[#02857E] hover:bg-gray-100 font-semibold px-8">
              <Link to="/auth?role=carrier">Get Started <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white text-white hover:bg-white/10 font-semibold px-8 bg-transparent">
              <Link to="/pricing?role=carrier">View Carrier Plans <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
          <p className="text-sm text-white/80 mt-8">LaneRunner for Carriers · $99.99/mo</p>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default CarriersPage;
